import { useEffect, useState } from "react";
import { Save, CheckCircle2, AlertCircle, ExternalLink, Target, Power } from "lucide-react";
import { getAdsSettings, updateAdsSettings } from "@/lib/admin-api";
import { toast } from "sonner";

const FIELDS = [
  { key: "conversion_label_form", label: "Conversión Formulario", hint: "Label de la conversión 'Contact / Lead form'", example: "abc123XYZ_01" },
  { key: "conversion_label_catalog", label: "Conversión Catálogo", hint: "Label de la conversión 'Catalog download'", example: "def456ABC_02" },
  { key: "conversion_label_whatsapp", label: "Conversión WhatsApp", hint: "Label de la conversión 'WhatsApp click'", example: "ghi789DEF_03" },
  { key: "conversion_label_phone", label: "Conversión Teléfono", hint: "Label de la conversión 'Phone call click'", example: "jkl012GHI_04" },
];

export default function AdminAdsSetup() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    google_ads_id: "",
    conversion_label_form: "",
    conversion_label_catalog: "",
    conversion_label_whatsapp: "",
    conversion_label_phone: "",
    enabled: true,
  });
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const cfg = await getAdsSettings();
      setForm({
        google_ads_id: cfg.google_ads_id || "",
        conversion_label_form: cfg.conversion_label_form || "",
        conversion_label_catalog: cfg.conversion_label_catalog || "",
        conversion_label_whatsapp: cfg.conversion_label_whatsapp || "",
        conversion_label_phone: cfg.conversion_label_phone || "",
        enabled: cfg.enabled !== false,
      });
      setUpdatedAt(cfg.updated_at || null);
    } catch {
      toast.error("Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      payload.google_ads_id = (payload.google_ads_id || "").trim() || null;
      for (const f of FIELDS) {
        payload[f.key] = (payload[f.key] || "").trim() || null;
      }
      await updateAdsSettings(payload);
      toast.success("Configuración guardada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const isValidId = !form.google_ads_id || /^AW-\d{6,}$/i.test(form.google_ads_id.trim());
  const ready = !!form.google_ads_id &&
    isValidId &&
    FIELDS.every((f) => (form[f.key] || "").trim().length > 0) &&
    form.enabled;

  if (loading) return <div className="p-8 text-neutral-500">Cargando...</div>;

  return (
    <div className="p-5 md:p-8 max-w-3xl" data-testid="admin-ads-setup-page">
      <div className="mb-6">
        <h1 className="section-title text-3xl md:text-4xl font-black flex items-center gap-2">
          <Target className="w-7 h-7 text-red-600" /> Google Ads — Conversiones
        </h1>
        <p className="text-neutral-500 text-sm mt-1">
          Configura tu ID de conversiones y los 4 labels. Se activan inmediatamente en la landing,
          sin redeploy.
        </p>
      </div>

      {/* Status banner */}
      <div
        className={`mb-5 rounded-2xl border p-4 flex items-start gap-3 ${
          ready ? "bg-emerald-50 border-emerald-200" : "bg-orange-50 border-orange-200"
        }`}
        data-testid="ads-status-banner"
      >
        {ready ? (
          <>
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-emerald-800 text-sm">Conversiones activas</p>
              <p className="text-xs text-emerald-700">
                Google Ads ya recibe tus conversiones (Form, Catálogo, WhatsApp, Teléfono). Verifica en Google Ads → Herramientas → Conversiones.
              </p>
            </div>
          </>
        ) : (
          <>
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-orange-800 text-sm">Pendiente de configurar</p>
              <p className="text-xs text-orange-700">
                Ingresa tu ID de Google Ads y los 4 labels para empezar a medir conversiones y optimizar tus campañas.
              </p>
            </div>
          </>
        )}
      </div>

      <form onSubmit={onSave} className="space-y-5 bg-white rounded-2xl border border-neutral-200 p-5 md:p-6">
        {/* AW ID */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1.5">
            ID de conversiones Google Ads
          </label>
          <input
            type="text"
            value={form.google_ads_id}
            onChange={(e) => setForm({ ...form, google_ads_id: e.target.value })}
            placeholder="AW-123456789"
            className="form-input w-full font-mono"
            data-testid="ads-id-input"
          />
          <p className="text-[11px] text-neutral-500 mt-1">
            En Google Ads → Herramientas → Conversiones → cualquier conversión → Etiqueta de conversión. Copia la
            parte <code>AW-XXXXXXXXX</code> (sin el “/label”).
          </p>
          {!isValidId && (
            <p className="text-xs text-red-600 mt-1">El ID debe tener el formato <code>AW-123456789</code>.</p>
          )}
        </div>

        {/* 4 Labels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-600 mb-1.5">
                {f.label}
              </label>
              <input
                type="text"
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.example}
                className="form-input w-full font-mono"
                data-testid={`ads-${f.key}-input`}
              />
              <p className="text-[11px] text-neutral-500 mt-1">{f.hint}</p>
            </div>
          ))}
        </div>

        {/* Enabled toggle */}
        <div className="flex items-center justify-between border border-neutral-200 rounded-xl p-3">
          <div className="flex items-center gap-2">
            <Power className={`w-4 h-4 ${form.enabled ? "text-emerald-600" : "text-neutral-400"}`} />
            <div>
              <p className="text-sm font-semibold">
                {form.enabled ? "Activado" : "Desactivado"}
              </p>
              <p className="text-xs text-neutral-500">
                Cuando está desactivado, la landing NO carga el script de Google Ads ni envía conversiones.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setForm({ ...form, enabled: !form.enabled })}
            className={`relative w-12 h-6 rounded-full transition ${form.enabled ? "bg-emerald-500" : "bg-neutral-300"}`}
            data-testid="ads-enabled-toggle"
            aria-label="Toggle activación"
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${form.enabled ? "left-6" : "left-0.5"}`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-neutral-500">
            {updatedAt ? `Última actualización: ${new Date(updatedAt).toLocaleString("es-MX")}` : "Sin cambios previos"}
          </div>
          <button
            type="submit"
            disabled={saving || !isValidId}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm disabled:opacity-60"
            data-testid="ads-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>

      {/* Ayuda */}
      <div className="mt-6 bg-neutral-50 border border-neutral-200 rounded-2xl p-5 text-sm text-neutral-700 space-y-2">
        <h3 className="font-bold text-neutral-900 flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> ¿Cómo configurar conversiones en Google Ads?
        </h3>
        <ol className="list-decimal ml-5 space-y-1 text-[13px]">
          <li>Entra a Google Ads → <strong>Herramientas</strong> → <strong>Conversiones</strong>.</li>
          <li>Crea 4 conversiones tipo <em>Página web</em>: Form, Catálogo, WhatsApp y Teléfono.</li>
          <li>En cada una copia la “Etiqueta de conversión” (ej: <code className="bg-neutral-200 px-1 rounded">abc123XYZ_01</code>).</li>
          <li>Copia también el ID de conversiones (<code className="bg-neutral-200 px-1 rounded">AW-123456789</code>) desde cualquiera de ellas.</li>
          <li>Pégalos aquí arriba y guarda. Listo — las conversiones se disparan automáticamente cuando un visitante convierte.</li>
        </ol>
        <p className="text-xs text-neutral-500 pt-2">
          Consejo: usa el modo <strong>Preview de Google Tag Assistant</strong> para verificar que los eventos se reciben correctamente.
        </p>
      </div>
    </div>
  );
}
